import { StyleSheet, View } from "react-native";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  CheckBox,
  IndexPath,
  Radio,
  Select,
  SelectItem,
  Text,
} from "@ui-kitten/components";
import {
  EventDetails,
  PlanPackageComponentDetails,
  SubscriptionDetails,
} from "features/types";
import { RootStackParamList } from "navigation/types";
import { RouteProp, useRoute } from "@react-navigation/native";
import { useDebouncedCallback } from "use-debounce";
import toast from "utils/toast";
import ConfirmPurchaseButton from "components/ConfirmPurchaseButton";
import Button from "components/Button";
import _ from "lodash";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import { MAX_GUEST_SEATS } from "constants/";
import Spacer from "components/Spacer";
import TDCDialog from "screens/Schedule/components/TDCDialog";
import Input from "components/Input";
import useCurrentUser from "hooks/useCurrentUser";
import {
  getRemainingAvailableSlotsForComponent,
  getRemainingPerEventMaxForComponent,
  usersNotGoing,
} from "screens/Reserve/helpers";
import { useAccount } from "hooks/useAccount";
import { useMutation } from "@tanstack/react-query";
import {
  useCalculateGroupChargeQuery,
  groupReserveTicketsFetcher,
  useFetchGroupDetailsQuery,
  CalculateGroupChargeItem,
  GroupReserveOrRedeemItem,
} from "queries/ticket.queries";
import i18n from "plugins/localization";
import { toDashboard } from "utils/navigationUtils";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import FanRallyLogo from "components/FanRallyLogo";
import PriceDetails from "screens/Reserve/components/PriceDetails";
import { useAppSelector } from "hooks/useAppSelector";
import { setSelectedSubscription } from "features/slices/reservationFlowSlice";
import { useAppDispatch } from "hooks/useAppDispatch";
import {
  filterIneligibleSubscriptions,
  getUniqSubscriptions,
} from "utils/subscriptionUtils";
import { getPlanType } from "utils/planPackageUtils";
import { getUserName } from "utils/userUtils";

const findSubsByType = (
  subscriptions: SubscriptionDetails[],
  type: PlanPackageComponentDetails["type"]
) => {
  return subscriptions.filter((sub) => {
    return (
      filterIneligibleSubscriptions(sub) &&
      getPlanType(sub.planPackage) === type
    );
  });
};

const StepThree = (props: { event: EventDetails }) => {
  const { event } = props;
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();

  const allowGroupRelease = tenantConfig?.allowGroupCodeReleases || false;

  const dispatch = useAppDispatch();
  const { account, accountEvents: events } = useAccount();
  const { selectedSubscription } = useAppSelector(
    (state) => state.reservationFlow
  );

  const [TDCDialogVisible, setTDCDialogVisible] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [numOfVouchers, setNumOfVouchers] = useState<string>("1");
  const [numOfBankSeats, setNumOfBankSeats] = useState<string>("1");

  const [guestComponentIds, setGuestComponentIds] = useState<number[]>([]);
  const route = useRoute<RouteProp<RootStackParamList, "GroupReservation">>();

  const planType = useMemo(() => {
    if (!selectedSubscription) return undefined;

    return getPlanType(selectedSubscription.planPackage);
  }, [selectedSubscription]);

  const { currentUser } = useCurrentUser();
  const { groupCode, eventCode } = route.params;

  const { data: groupDetails } = useFetchGroupDetailsQuery(
    {
      eventCode,
      groupCode,
    },
    {
      onError() {
        toDashboard();
      },
    }
  );

  const { mutateAsync: groupReserveTickets, isLoading: loading } = useMutation(
    groupReserveTicketsFetcher
  );

  const getCalculateGroupChargeItems = (): CalculateGroupChargeItem[] => {
    if (!selectedSubscription) return [];
    const { planPackage } = selectedSubscription;
    const result: CalculateGroupChargeItem[] = [];

    const primaryComponent = planPackage.primaryComponent;

    function pushItemToResult(
      planPackageComponentId: number,
      planPackageId: number
    ) {
      result.push({
        planPackageComponentId,
        planPackageId,
      });
    }

    if (planType === "RESERVATION") {
      selectedUsers.forEach(() => {
        pushItemToResult(primaryComponent.id, planPackage.id);
      });
    } else {
      const max = planType === "VOUCHER" ? numOfVouchers : numOfBankSeats;
      for (let i = 0; i < Number(max); i++) {
        pushItemToResult(primaryComponent.id, planPackage.id);
      }
    }

    guestComponentIds.forEach((componentId) => {
      pushItemToResult(componentId, planPackage.id);
    });
    return result;
  };

  const { data: charge, refetch: calculateGroupCharge } =
    useCalculateGroupChargeQuery({
      accountId: account?.id,
      eventId: event.id,
      groupCode,
      items: getCalculateGroupChargeItems(),
    });

  if (!groupCode) {
    setTimeout(() => {
      toDashboard();
    }, 250);
  }
  const uniqAccountSubscriptions = getUniqSubscriptions(
    account?.subscriptions || [],
    (sub: SubscriptionDetails) =>
      event === null ? true : sub.planPackage.eventTypes.includes(event?.type)
  );

  const reservationSubs = findSubsByType(
    account?.subscriptions || [],
    "RESERVATION"
  );

  const voucherSubs = findSubsByType(account?.subscriptions || [], "VOUCHER");
  const bankSubs = findSubsByType(account?.subscriptions || [], "BANK");

  const usersNotGoingIds = usersNotGoing({
    subscriptions: reservationSubs,
    eventId: groupDetails?.event.id,
    events,
  });

  const userGoingSubscriptions = reservationSubs.filter((sub) => {
    return (
      sub.planPackage.id === selectedSubscription?.planPackage?.id && // only show users who have the same plan package as the selected plan package
      !usersNotGoingIds.has(sub.member.user.id) // and whose id is not in the users not going list
    );
  });
  const userNotGoingSubscriptions = reservationSubs.filter((sub) =>
    usersNotGoingIds.has(sub.member.user.id)
  );

  const noOneCanGo =
    reservationSubs.length === userNotGoingSubscriptions.length;

  const accountVoucherMax = useMemo(() => {
    if (!selectedSubscription || !groupDetails || !account) return 0;
    const max = getRemainingAvailableSlotsForComponent({
      component: selectedSubscription.planPackage.primaryComponent,
      subscriptions: voucherSubs,
      events,
      eventId: groupDetails!.event.id,
      isChangingSeats: false,
    });
    return max;
  }, [selectedSubscription, groupDetails, account]);

  const accountBankPerEventMax = useMemo(() => {
    if (!selectedSubscription || !groupDetails || !account) return 0;
    const matchedSubs = findSubsByType(account.subscriptions, "BANK");
    return getRemainingPerEventMaxForComponent({
      component: selectedSubscription.planPackage.primaryComponent,
      subscriptions: matchedSubs,
      events,
      eventId: groupDetails.event.id,
      isChangingSeats: false,
    });
  }, [selectedSubscription, groupDetails, account]);

  const fetchCharge = useDebouncedCallback(async () => {
    if (!selectedSubscription || !account) return;

    const numTicketsExceptGuests =
      planType === "RESERVATION"
        ? selectedUsers.length
        : planType === "VOUCHER"
        ? Number(numOfVouchers)
        : Number(numOfBankSeats);

    const totalTickets = numTicketsExceptGuests + guestComponentIds.length;

    if (!totalTickets || totalTickets <= 0) return;
    if (planType === "VOUCHER" && numTicketsExceptGuests > accountVoucherMax)
      return;

    calculateGroupCharge();
  }, 250);

  const handleSelectUser = (userId: number) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers((users) => users.filter((id) => id !== userId));
    } else {
      setSelectedUsers((users) => [...users, userId]);
    }
  };

  const findNextAvailableGuestComponent = (
    components: PlanPackageComponentDetails[]
  ) => {
    if (!selectedSubscription) return undefined;

    const freeGuestComponents = components.filter(
      (component) => component.priceTableId === null
    );
    const paidGuestComponents = components.filter(
      (component) => component.priceTableId !== null
    );

    // if there are free guest components or paid guest components and have not reached the per event max, use them
    const guestComponents = [...freeGuestComponents, ...paidGuestComponents];

    for (const guestComponent of guestComponents) {
      const guestComponentAlreadySelectedAmount = guestComponentIds.filter(
        (id) => id === guestComponent.id
      ).length;

      const subscriptions =
        planType === "RESERVATION"
          ? userGoingSubscriptions
          : planType === "VOUCHER"
          ? voucherSubs
          : bankSubs;

      if (
        guestComponentAlreadySelectedAmount <
        getRemainingAvailableSlotsForComponent({
          component: guestComponent,
          subscriptions,
          events,
          eventId: event.id,
          isChangingSeats: false,
        })
      ) {
        return guestComponent;
      }
    }

    return undefined;
  };

  const guestComponents = useMemo(() => {
    if (!selectedSubscription) return [];
    return selectedSubscription.planPackage.components.filter((component) =>
      component.type.includes("GUEST")
    );
  }, [selectedSubscription]);

  const nextAvailableGuestComponent =
    findNextAvailableGuestComponent(guestComponents);

  const handleAddGuest = () => {
    if (!selectedSubscription) return;
    if (!nextAvailableGuestComponent) return;

    setGuestComponentIds((guestComponentIds) => [
      ...guestComponentIds,
      nextAvailableGuestComponent.id,
    ]);
  };

  const accountNotLinkedToTDC = () => {
    const barcodeProvider = tenantConfig?.barcodeProvider;
    if (barcodeProvider === "TDC" && account?.extUsername === null) {
      return true;
    }
    return false;
  };

  const handleSubmit = async () => {
    // Remember, that if TicketProvider == TDC, then the user MUST go through the TDC account linking process if it has not yet been completed!
    if (accountNotLinkedToTDC()) {
      setTDCDialogVisible(true);
      return;
    }
    await handleGroupReserve();
    toDashboard();
    toast.success(i18n.t("common.reservationConfirmedMessage"));
  };

  const getGroupReservationItems = (): GroupReserveOrRedeemItem[] => {
    const result: GroupReserveOrRedeemItem[] = [];
    if (!selectedSubscription) return [];

    const planPackageComponentId =
      selectedSubscription.planPackage.primaryComponent.id;
    if (planType === "RESERVATION") {
      selectedUsers.forEach((userId) =>
        result.push({
          planPackageComponentId,
          userId,
        })
      );
    } else {
      const max = planType === "VOUCHER" ? numOfVouchers : numOfBankSeats;
      for (let i = 0; i < Number(max); i++) {
        result.push({
          planPackageComponentId,
          // don't need to specify user id for voucher tickets
          userId: planType === "VOUCHER" ? undefined : currentUser!.id,
        });
      }
    }

    guestComponentIds.forEach((guestComponentId) => {
      result.push({
        planPackageComponentId: guestComponentId,
      });
    });

    return result;
  };
  const handleGroupReserve = async () => {
    await groupReserveTickets({
      accountId: account!.id,
      planPackageId: selectedSubscription!.planPackage.id,
      eventId: groupDetails!.event.id,
      groupCode,
      items: getGroupReservationItems(),
    });
  };

  const handleRemoveGuest = (guestIndex: number) => {
    setGuestComponentIds((guestComponentIds) => {
      const newGuestComponentIds = guestComponentIds.filter(
        (_, index) => index !== guestIndex
      );
      return newGuestComponentIds;
    });
  };

  const componentHasNotReachedMax = useCallback(
    (component: PlanPackageComponentDetails) => {
      if (!account) return [];

      const maxForComponent = getRemainingAvailableSlotsForComponent({
        component,
        subscriptions: account.subscriptions,
        events,
        eventId: event.id,
        isChangingSeats: false,
      });
      const componentAlreadySelectedAmount = guestComponentIds.filter(
        (id) => id === component.id
      ).length;

      return componentAlreadySelectedAmount < maxForComponent;
    },
    [account, events, guestComponentIds]
  );

  const getComponentName = (componentId: number) =>
    guestComponents.find((component) => component.id === componentId)?.name ||
    "";

  // returns a list of guest components that have not reached their per event max
  const componentsHaveNotReachedMax = guestComponents.filter(
    componentHasNotReachedMax
  );

  const handleSwitchComponent = (
    component: PlanPackageComponentDetails,
    guestComponentsIndex: number
  ) => {
    setGuestComponentIds((guestComponentIds) => {
      return guestComponentIds.map((id, index) => {
        if (index !== guestComponentsIndex) {
          return id;
        } else {
          return component.id;
        }
      });
    });
  };

  const numOfVouchersValid = useMemo(() => {
    if (
      Number(numOfVouchers) <= 0 ||
      Number(numOfVouchers) > accountVoucherMax
    ) {
      return false;
    }
    return true;
  }, [numOfVouchers, accountVoucherMax]);

  const numOfBankSeatsValid = useMemo(() => {
    if (
      Number(numOfBankSeats) <= 0 ||
      Number(numOfBankSeats) > accountBankPerEventMax
    ) {
      return false;
    }
    return true;
  }, [numOfBankSeats, accountBankPerEventMax]);

  // returns true if button should be disabled
  const checkDisabled = useMemo(() => {
    if (loading || !selectedSubscription) return true;

    switch (planType) {
      case "RESERVATION":
        return selectedUsers.length === 0;
      case "VOUCHER":
        return !numOfVouchersValid;
      case "BANK":
        return !numOfBankSeatsValid;
      default:
        return false;
    }
  }, [selectedSubscription, selectedUsers, numOfVouchers, numOfBankSeats]);

  const shouldShowPrice = useMemo(() => {
    if (!selectedSubscription) return false;
    if (planType === "RESERVATION") {
      if (selectedUsers.length === 0) return false;
    }
    return charge !== undefined && parseFloat(charge.totalPrice) > 0;
  }, [selectedSubscription, selectedUsers, charge]);

  useEffect(() => {
    if (!planType) return;
    switch (planType) {
      case "RESERVATION":
        if (selectedUsers.length === 0) return;
        break;
      case "VOUCHER":
        if (!numOfVouchersValid) return;
        break;
      case "BANK":
        if (!numOfBankSeatsValid) return;
        break;
      default:
        break;
    }
    fetchCharge();
  }, [
    selectedSubscription,
    selectedUsers,
    guestComponentIds,
    numOfVouchers,
    numOfBankSeats,
  ]);

  if (!event || !account)
    return (
      <Text category="h4" style={{ marginTop: 15 }}>
        {i18n.t("common.loading")}
      </Text>
    );

  return (
    <View>
      <BaseCard title="Attendees" style={{ marginVertical: 15 }}>
        {groupDetails !== undefined &&
          groupDetails.tickets.map((ticket, index) => {
            const {
              accountId,
              reservedByAccountId,
              firstName,
              lastName,
              id,
              planPackageComponentType,
            } = ticket;
            const title =
              planPackageComponentType.includes("GUEST") &&
              accountId === reservedByAccountId
                ? i18n.t("screens.GroupReservation.guestPending")
                : `${firstName} ${lastName}`;
            return (
              <BaseCardListItem
                key={id}
                title={`${index + 1}. ${title}`}
                showArrow={false}
              ></BaseCardListItem>
            );
          })}
      </BaseCard>

      <Text category="label" style={{ marginVertical: 10 }}>
        {i18n.t("screens.GroupReservation.selectPlan")}
      </Text>
      <View>
        {uniqAccountSubscriptions.length === 0 && (
          <Text status="danger">
            {i18n.t("screens.GroupReservation.noValidPlan")}
          </Text>
        )}
        {uniqAccountSubscriptions.map((sub) => (
          <View key={sub.id} style={{ marginBottom: 5 }}>
            <Radio
              testID="groupReservation-planPackage-radio"
              checked={
                selectedSubscription?.planPackage.id === sub.planPackage.id
              }
              onChange={() => {
                setGuestComponentIds([]);
                dispatch(setSelectedSubscription(sub));
              }}
            >
              {sub.planPackage.name}
            </Radio>
          </View>
        ))}
      </View>

      {allowGroupRelease === false && (
        <Text status="danger">
          {i18n.t("common.groupReservationCannotBeChanged")}
        </Text>
      )}

      <Spacer />
      {selectedSubscription !== null && (
        <View>
          {planType === "RESERVATION" && (
            <View style={{ marginVertical: 15 }}>
              <View>
                <Text category="h6">
                  {i18n.t("screens.GroupReservation.whoIsGoing")}
                </Text>
                <Spacer />
                {/* for users who can go to this event */}
                {userGoingSubscriptions.map((sub) => (
                  <View key={sub.id} style={styles.checkboxContainer}>
                    <CheckBox
                      testID="groupReservation-user-checkbox"
                      checked={selectedUsers.includes(sub.member.user.id)}
                      onChange={() => handleSelectUser(sub.member.user.id)}
                    >
                      {getUserName(sub.member)}
                    </CheckBox>
                  </View>
                ))}
                {/* for users who can't go to this event */}
                {reservationSubs
                  .filter((sub) => usersNotGoingIds.has(sub.member.user.id))
                  .map((sub) => (
                    <View key={sub.id} style={styles.checkboxContainer}>
                      <CheckBox checked={true} disabled>
                        {getUserName(sub.member)}
                      </CheckBox>
                    </View>
                  ))}

                {noOneCanGo && (
                  <Text category="c1" status="danger">
                    {i18n.t("screens.GroupReservation.optionDisabled")}
                  </Text>
                )}
              </View>
            </View>
          )}
          {planType === "VOUCHER" && (
            <View>
              <Text category="h6">
                {i18n.t("screens.GroupReservation.howManySeats", {
                  max: accountVoucherMax,
                })}
              </Text>
              <View style={{ marginVertical: 10 }}>
                <View>
                  <Input
                    value={numOfVouchers}
                    onChangeText={setNumOfVouchers}
                  ></Input>
                  {accountVoucherMax <= 0 && (
                    <Text status="danger">
                      {i18n.t("screens.GroupReservation.notEnoughVouchers")}
                    </Text>
                  )}
                </View>
              </View>
            </View>
          )}
          {planType === "BANK" && (
            <View>
              <Text category="h6">
                {i18n.t("screens.GroupReservation.howManySeats", {
                  max: accountBankPerEventMax,
                })}
              </Text>
              <View style={{ marginVertical: 10 }}>
                <View>
                  <Input
                    value={numOfBankSeats}
                    onChangeText={setNumOfBankSeats}
                  ></Input>
                </View>
              </View>
            </View>
          )}
        </View>
      )}

      {guestComponents.length > 0 &&
        guestComponentIds.length < MAX_GUEST_SEATS &&
        nextAvailableGuestComponent !== undefined && (
          <Button
            style={{ alignSelf: "flex-end" }}
            size="small"
            onPress={handleAddGuest}
          >
            Add Guest
          </Button>
        )}

      {guestComponentIds.length > 0 && (
        <View>
          <Text category="h6" style={{ marginVertical: 10 }}>
            Guest
          </Text>
          {guestComponentIds.map((id, index) => (
            <BaseCard
              style={{ marginBottom: 10 }}
              key={index}
              title={
                <View
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <Text>Guest {index + 1}</Text>
                  <Button size="tiny" onPress={() => handleRemoveGuest(index)}>
                    {i18n.t("screens.GroupReservation.remove")}
                  </Button>
                </View>
              }
            >
              <Spacer></Spacer>
              <Select
                value={getComponentName(id)}
                onSelect={(selectItemIndex) => {
                  handleSwitchComponent(
                    componentsHaveNotReachedMax[
                      (selectItemIndex as IndexPath).row
                    ],
                    index
                  );
                }}
              >
                {componentsHaveNotReachedMax.map((component) => (
                  <SelectItem
                    key={component.id}
                    title={component.name}
                    onPressIn={() => {
                      setGuestComponentIds(guestComponentIds);
                    }}
                  ></SelectItem>
                ))}
              </Select>

              <View
                style={{
                  marginTop: 10,
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <FanRallyLogo width={50} height={35} />
                <Text category="s1">
                  {getComponentName(id)} powered by FanRally
                </Text>
              </View>
            </BaseCard>
          ))}
        </View>
      )}

      {selectedSubscription && (
        <View style={{ marginVertical: 20 }}>
          {charge && shouldShowPrice && (
            <PriceDetails
              charge={charge}
              planPackage={selectedSubscription.planPackage}
            />
          )}
        </View>
      )}

      <View style={{ marginVertical: 10 }}>
        <ConfirmPurchaseButton
          charge={Number(charge?.totalCreditCardCharges || "0")}
          disabled={checkDisabled || loading}
          loading={loading}
          onPress={handleSubmit}
          buttonText={
            Number(charge?.totalPrice || "0") === 0
              ? i18n.t("common.reserve")
              : i18n.t("common.purchaseAndReserve")
          }
        />
      </View>

      <TDCDialog
        visible={TDCDialogVisible}
        setVisible={setTDCDialogVisible}
        cancelText={i18n.t("common.close")}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  checkboxContainer: {
    marginVertical: 3,
  },
});
export default StepThree;
